import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ThumbsUp, Minus, ThumbsDown, Send } from "lucide-react";

/**
 * WipInterestDialog — mini pre-reservation form shown when a partner clicks
 * a greyed-out solution card (FortiWeb, FortiSIEM, …). Captures their
 * commercial interest (oui / peut-être / non) + an optional note and
 * persists it via POST /api/wip/interest. The product team can later
 * aggregate signals via GET /api/admin/wip/interest/summary.
 *
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   solutionId — string (lowercase id, e.g. "fortiweb")
 *   solutionName — string (display name)
 */
const OPTIONS = [
  { value: "yes",   label: "Oui",       sub: "Mes clients en demandent",     icon: ThumbsUp,   active: "bg-emerald-600 text-white border-emerald-600", idle: "border-emerald-300 text-emerald-700 hover:bg-emerald-50" },
  { value: "maybe", label: "Peut-être", sub: "Cas d'usage potentiel",        icon: Minus,      active: "bg-amber-500 text-white border-amber-500",     idle: "border-amber-300 text-amber-700 hover:bg-amber-50" },
  { value: "no",    label: "Non",       sub: "Pas pertinent pour mon parc",  icon: ThumbsDown, active: "bg-zinc-700 text-white border-zinc-700",       idle: "border-zinc-300 text-zinc-600 hover:bg-zinc-50" },
];

export default function WipInterestDialog({ open, onClose, solutionId, solutionName }) {
  const [interest, setInterest] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [previousAnswer, setPreviousAnswer] = useState(null);

  // Reset state every time the dialog opens for a (potentially new) solution.
  useEffect(() => {
    if (!open || !solutionId) return;
    setInterest(null);
    setNote("");
    setPreviousAnswer(null);
    // Look up the partner's previous answer (if any) to pre-fill.
    api.get("/wip/interest/mine")
      .then((r) => {
        const prev = r.data?.by_solution?.[solutionId];
        if (prev) {
          setInterest(prev.interest);
          setNote(prev.note || "");
          setPreviousAnswer(prev);
        }
      })
      .catch(() => {});
  }, [open, solutionId]);

  const submit = async () => {
    if (!interest) {
      toast.error("Choisissez Oui, Peut-être ou Non.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wip/interest", {
        solution_id: solutionId,
        interest,
        note: note.trim() || undefined,
      });
      toast.success(`Merci ! Votre intérêt pour ${solutionName} a été enregistré.`);
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="wip-interest-dialog">
        <DialogHeader>
          <DialogTitle>
            {solutionName}
            <span className="ml-2 text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 align-middle">
              En cours de développement
            </span>
          </DialogTitle>
          <DialogDescription className="pt-1">
            Ce module n'est pas encore disponible. <strong>Signalez-nous votre intérêt</strong> commercial : vos réponses pilotent la roadmap produit.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 mb-2 block">
            Êtes-vous intéressé par ce module ?
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = interest === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setInterest(opt.value)}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-sm border-2 transition-colors text-sm font-semibold ${active ? opt.active : `bg-white ${opt.idle}`}`}
                  data-testid={`wip-interest-${opt.value}`}
                  aria-pressed={active}
                >
                  <Icon className="h-4 w-4" />
                  <span>{opt.label}</span>
                  <span className={`text-[10px] font-normal leading-tight text-center ${active ? "text-white/80" : "text-zinc-500"}`}>
                    {opt.sub}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <Label htmlFor="wip-note" className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 mb-1 block">
              Commentaire (optionnel) — cas d'usage, taille de parc, urgence…
            </Label>
            <Textarea
              id="wip-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex. Client banque, 3 sites, projet AO en Q3 2026…"
              rows={3}
              maxLength={500}
              className="rounded-sm text-sm"
              data-testid="wip-interest-note"
            />
            <p className="mt-1 text-[10px] text-zinc-500 text-right tabular-nums">{note.length}/500</p>
          </div>

          {previousAnswer && (
            <p className="mt-2 text-[11px] text-zinc-500 italic" data-testid="wip-interest-prev">
              Votre dernière réponse : <strong>{OPTIONS.find((o) => o.value === previousAnswer.interest)?.label}</strong>
              {previousAnswer.submitted_at ? ` · ${new Date(previousAnswer.submitted_at).toLocaleDateString("fr-FR")}` : ""}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="rounded-sm"
            data-testid="wip-interest-cancel"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !interest}
            className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
            data-testid="wip-interest-submit"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {submitting ? "Envoi…" : previousAnswer ? "Mettre à jour" : "Envoyer mon intérêt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
